// mindmetal-app/web/portal/src/pages/employee/mindmetal/MetricsTab.tsx
import { motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import { BarChart3, TrendingUp, Users, Activity, Database } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent, Badge, api } from '@personic/shared'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MetricsResponse {
  total_profiles: number
  active_profiles: number
  coverage_distribution: Record<string, number>
  avg_observations: number
  profiles_distilled: number
  distillation_rate: number
}

interface DistributionResponse {
  observation_histogram: { range: string; count: number }[]
}

// ---------------------------------------------------------------------------
// Animation variants
// ---------------------------------------------------------------------------

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } },
}

// ---------------------------------------------------------------------------
// Stat Card
// ---------------------------------------------------------------------------

interface StatCardProps {
  icon: React.ReactNode
  label: string
  value: string | number
  sub?: string
}

function StatCard({ icon, label, value, sub }: StatCardProps) {
  return (
    <motion.div variants={itemVariants}>
      <Card className="h-full">
        <CardContent className="p-5">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-warm-500 mb-1">{label}</p>
              <p className="text-3xl font-bold text-midnight-900">{value}</p>
              {sub && <p className="text-xs text-warm-400 mt-1">{sub}</p>}
            </div>
            <div className="w-10 h-10 rounded-lg bg-copper-50 flex items-center justify-center flex-shrink-0">
              {icon}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}

// ---------------------------------------------------------------------------
// Coverage Bar
// ---------------------------------------------------------------------------

const COVERAGE_COLORS: Record<string, string> = {
  minimal: 'from-warm-300 to-warm-400',
  emerging: 'from-copper-300 to-copper-400',
  established: 'from-copper-400 to-copper-500',
  mature: 'from-copper-500 to-copper-600',
}

const COVERAGE_BADGES: Record<string, 'default' | 'copper' | 'sage' | 'midnight'> = {
  minimal: 'default',
  emerging: 'copper',
  established: 'sage',
  mature: 'midnight',
}

function CoverageBar({
  level,
  count,
  total,
}: {
  level: string
  count: number
  total: number
}) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0
  const gradient = COVERAGE_COLORS[level] || 'from-warm-300 to-warm-400'
  const badgeVariant = COVERAGE_BADGES[level] || 'default'

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-midnight-700 capitalize">{level}</span>
          <Badge variant={badgeVariant} size="sm">
            {pct}%
          </Badge>
        </div>
        <span className="text-sm text-warm-500 tabular-nums">{count}</span>
      </div>
      <div className="h-2 bg-warm-100 rounded-full overflow-hidden">
        <motion.div
          className={`h-full bg-gradient-to-r ${gradient} rounded-full`}
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.6, ease: 'easeOut', delay: 0.2 }}
        />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Custom Recharts Tooltip
// ---------------------------------------------------------------------------

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-white border border-warm-200 rounded-lg shadow-lg p-3">
      <p className="text-xs font-medium text-midnight-800 mb-1">{label}</p>
      <p className="text-sm font-semibold text-copper-600">
        {payload[0].value.toLocaleString()} profiles
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// MetricsTab Component
// ---------------------------------------------------------------------------

export function MetricsTab() {
  const {
    data: metrics,
    isLoading: metricsLoading,
  } = useQuery<MetricsResponse>({
    queryKey: ['mindmetal-metrics'],
    queryFn: async () => {
      const res = await api.get('/admin/mindmetal/metrics')
      return res.data
    },
  })

  const {
    data: distribution,
    isLoading: distributionLoading,
  } = useQuery<DistributionResponse>({
    queryKey: ['mindmetal-metrics-distribution'],
    queryFn: async () => {
      const res = await api.get('/admin/mindmetal/metrics/distribution')
      return res.data
    },
  })

  if (metricsLoading || distributionLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-copper-600" />
      </div>
    )
  }

  const distillationRateDisplay =
    metrics?.distillation_rate != null
      ? `${(metrics.distillation_rate * 100).toFixed(1)}%`
      : '--'

  const avgObsDisplay =
    metrics?.avg_observations != null
      ? metrics.avg_observations.toFixed(1)
      : '--'

  const totalForCoverage = metrics?.active_profiles || 1

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<Database className="w-5 h-5 text-copper-600" />}
          label="Total Profiles"
          value={metrics?.total_profiles?.toLocaleString() ?? '--'}
          sub={`${metrics?.profiles_distilled?.toLocaleString() ?? 0} distilled`}
        />
        <StatCard
          icon={<Users className="w-5 h-5 text-copper-600" />}
          label="Active Profiles"
          value={metrics?.active_profiles?.toLocaleString() ?? '--'}
        />
        <StatCard
          icon={<TrendingUp className="w-5 h-5 text-copper-600" />}
          label="Distillation Rate"
          value={distillationRateDisplay}
          sub="of active profiles"
        />
        <StatCard
          icon={<Activity className="w-5 h-5 text-copper-600" />}
          label="Avg Observations"
          value={avgObsDisplay}
          sub="per profile"
        />
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Coverage Distribution */}
        <motion.div variants={itemVariants}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-copper-600" />
                Coverage Distribution
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {metrics?.coverage_distribution ? (
                (['minimal', 'emerging', 'established', 'mature'] as const).map((level) => (
                  <CoverageBar
                    key={level}
                    level={level}
                    count={metrics.coverage_distribution[level] || 0}
                    total={totalForCoverage}
                  />
                ))
              ) : (
                <p className="text-sm text-warm-400 text-center py-6">No distribution data available</p>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Observation Histogram */}
        <motion.div variants={itemVariants}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-copper-600" />
                Observation Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              {distribution?.observation_histogram?.length ? (
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={distribution.observation_histogram}>
                      <XAxis
                        dataKey="range"
                        tick={{ fontSize: 11 }}
                        stroke="#a3998e"
                        tickLine={false}
                        axisLine={{ stroke: '#e5e0db' }}
                      />
                      <YAxis
                        tick={{ fontSize: 11 }}
                        stroke="#a3998e"
                        tickLine={false}
                        axisLine={{ stroke: '#e5e0db' }}
                        allowDecimals={false}
                      />
                      <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(194, 124, 62, 0.08)' }} />
                      <Bar
                        dataKey="count"
                        fill="#c27c3e"
                        radius={[4, 4, 0, 0]}
                        maxBarSize={48}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-sm text-warm-400 text-center py-12">No histogram data available</p>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </motion.div>
  )
}
